import { useState, useEffect, useMemo, useRef } from 'react';
import useSWR from 'swr';
import {
    ANALYTICS_EVENTS,
    captureEvent,
    programFacilityProps
} from '@/lib/events';
import { useDebounceValue } from 'usehooks-ts';
import { Search, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/api/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { FormModal } from '@/components/shared';
import { User, ConflictDetail, ServerResponseMany } from '@/types';

interface EnrollResidentsModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    classId: number;
    classFacilityId: number;
    className: string;
    capacity: number;
    enrolled: number;
    onEnrolled: () => void;
}

export function EnrollResidentsModal({
    open,
    onOpenChange,
    classId,
    classFacilityId,
    className,
    capacity,
    enrolled,
    onEnrolled
}: EnrollResidentsModalProps) {
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [conflictMap, setConflictMap] = useState<Map<number, ConflictDetail>>(
        new Map()
    );
    const [searchTerm, setSearchTerm] = useState('');
    const [searchQuery] = useDebounceValue(searchTerm, 300);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const selectionFiredRef = useRef(false);
    const wasOpenRef = useRef(false);

    useEffect(() => {
        // Only fire on the closed→open transition — prop changes (capacity /
        // enrolled from parent revalidation) while the modal stays open must
        // not re-emit the open event or reset the first-selection guard.
        if (open && !wasOpenRef.current) {
            selectionFiredRef.current = false;
            captureEvent(ANALYTICS_EVENTS.EnrollModalOpened, {
                class_id: classId,
                capacity,
                enrolled,
                ...programFacilityProps(classFacilityId)
            });
        }
        wasOpenRef.current = open;
    }, [open, classId, classFacilityId, capacity, enrolled]);

    const encodedSearch = encodeURIComponent(searchQuery);
    const { data: usersResp } = useSWR<ServerResponseMany<User>>(
        open
            ? `/api/users?search=${encodedSearch}&per_page=50&order_by=name_last&order=asc&class_id=${classId}&facility_id=${classFacilityId}&include=only_unenrolled`
            : null
    );
    const users = useMemo(() => usersResp?.data ?? [], [usersResp?.data]);

    useEffect(() => {
        if (!open || users.length === 0) return;
        const userIds = users.map((u) => u.id);
        void API.post<{ conflicts: ConflictDetail[] }, { user_ids: number[] }>(
            `program-classes/${classId}/enrollment-conflicts`,
            {
                user_ids: userIds
            }
        ).then((resp) => {
            if (resp.success && resp.type === 'one') {
                const map = new Map<number, ConflictDetail>();
                for (const c of resp.data.conflicts ?? []) {
                    map.set(c.user_id, c);
                }
                setConflictMap(map);
            }
        });
    }, [open, users, classId]);

    const spotsAvailable = capacity - enrolled;
    const selectedCount = selectedIds.size;
    const remainingSpots = Math.max(0, spotsAvailable - selectedCount);
    const wouldExceedCapacity = selectedCount > spotsAvailable;

    const toggleUser = (id: number) => {
        const next = new Set(selectedIds);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        if (next.size > 0 && !selectionFiredRef.current) {
            selectionFiredRef.current = true;
            captureEvent(ANALYTICS_EVENTS.EnrollResidentsSelected, {
                class_id: classId,
                ...programFacilityProps(classFacilityId)
            });
        }
        setSelectedIds(next);
    };

    const handleClose = () => {
        setSelectedIds(new Set());
        setConflictMap(new Map());
        setSearchTerm('');
        setError('');
        setIsSubmitting(false);
        onOpenChange(false);
    };

    const handleEnroll = async () => {
        if (selectedIds.size === 0) return;
        setIsSubmitting(true);
        setError('');
        const userIds = Array.from(selectedIds);
        const resp = await API.post<
            unknown,
            { user_ids: number[]; confirm: boolean }
        >(`program-classes/${classId}/enrollments`, {
            user_ids: userIds,
            confirm: true
        });

        if (resp.success) {
            captureEvent(ANALYTICS_EVENTS.EnrollCompleted, {
                class_id: classId,
                selected_count: userIds.length,
                ...programFacilityProps(classFacilityId)
            });
            toast.success(
                `${userIds.length} ${userIds.length === 1 ? 'resident' : 'residents'} enrolled in ${className}`
            );
            onEnrolled();
            handleClose();
        } else {
            setError(resp.message || 'Failed to enroll residents');
        }
        setIsSubmitting(false);
    };

    return (
        <FormModal
            open={open}
            onOpenChange={(isOpen) => {
                if (!isOpen) handleClose();
            }}
            title={`Enroll Residents in ${className}`}
            description="Select residents to enroll in this class. You can select multiple residents at once."
            className="max-w-3xl max-h-[85vh] flex flex-col p-0"
            titleClassName="text-foreground text-xl"
            descriptionClassName="text-base"
            headerClassName="px-6 pt-6 pb-4"
        >
            <div className="px-6 pb-4">
                <div className="relative">
                    <Search className="input-icon-left size-4" />
                    <Input
                        placeholder="Search by resident ID or name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                    />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto border-t border-b border-gray-200">
                {users.length === 0 ? (
                    <div className="text-center py-16 text-gray-500">
                        <Search className="size-12 mx-auto mb-3 text-gray-300" />
                        <p className="font-medium">No residents found</p>
                        <p className="text-sm mt-1">
                            Try adjusting your search
                        </p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-200">
                        {users.map((user) => {
                            const conflict = conflictMap.get(user.id);
                            const isSelected = selectedIds.has(user.id);
                            return (
                                <label
                                    key={user.id}
                                    className={`flex items-center gap-4 px-6 py-4 cursor-pointer transition-colors ${
                                        isSelected
                                            ? 'bg-brand/5'
                                            : 'hover:bg-gray-50'
                                    }`}
                                >
                                    <Checkbox
                                        checked={isSelected}
                                        onCheckedChange={() =>
                                            toggleUser(user.id)
                                        }
                                        className="shrink-0"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 mb-1">
                                            <span className="font-medium text-brand-dark">
                                                {user.doc_id}
                                            </span>
                                            <span className="text-gray-700">
                                                {user.name_first}{' '}
                                                {user.name_last}
                                            </span>
                                        </div>
                                        {conflict && (
                                            <div className="flex items-start gap-2 text-sm text-amber-700">
                                                <AlertTriangle className="size-4 shrink-0 mt-0.5" />
                                                <span>
                                                    Already enrolled in &quot;
                                                    {conflict.conflicting_class}
                                                    &quot;
                                                    {conflict.conflict_days
                                                        ?.length > 0 &&
                                                        ` on ${conflict.conflict_days.join(', ')}`}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="section-footer">
                <div className="flex items-center justify-between mb-4">
                    <div className="text-sm">
                        <div className="flex items-center gap-6">
                            <div>
                                <span className="text-gray-600">
                                    Currently Enrolled:{' '}
                                </span>
                                <span className="font-semibold text-brand-dark">
                                    {enrolled} / {capacity}
                                </span>
                            </div>
                            <div>
                                <span className="text-gray-600">
                                    Remaining Spots:{' '}
                                </span>
                                <span
                                    className={`font-semibold ${remainingSpots === 0 ? 'text-red-600' : 'text-brand'}`}
                                >
                                    {remainingSpots}
                                </span>
                            </div>
                            <div>
                                <span className="text-gray-600">
                                    Selected:{' '}
                                </span>
                                <span
                                    className={`font-semibold ${wouldExceedCapacity ? 'text-red-600' : 'text-brand-dark'}`}
                                >
                                    {selectedCount}
                                </span>
                            </div>
                        </div>
                        {wouldExceedCapacity && (
                            <div className="text-red-600 font-medium flex items-center gap-2 mt-1">
                                <AlertTriangle className="size-4" />
                                Selection exceeds available capacity by{' '}
                                {selectedCount - spotsAvailable}
                            </div>
                        )}
                    </div>
                </div>

                {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

                <div className="flex gap-3 justify-end">
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => void handleEnroll()}
                        disabled={
                            selectedIds.size === 0 ||
                            isSubmitting ||
                            wouldExceedCapacity
                        }
                        variant="brand"
                    >
                        {isSubmitting
                            ? 'Enrolling...'
                            : `Enroll Selected (${selectedIds.size})`}
                    </Button>
                </div>
            </div>
        </FormModal>
    );
}
